import axios from 'axios';
import { call, put, take, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { PayloadAction } from '@reduxjs/toolkit';
import {
	getShopGoodsRequest,
	getShopGoodsSuccess,
	getShopsRequest,
	getShopsSuccess,
	addShopRequest,
	addShopSuccess,
	deleteShopSuccess,
	deleteShopRequest,
	getShopAttributesFormRequest,
	getShopAttributesFormSuccess,
	getShopAttributesFormFailure,
	updateShopAttributesRequest,
	updateShopAttributesSuccess,
	updateShopAttributesFailure,
	migrateShopRequest,
	migrateShopSuccess,
	migrateShopFailure,
	addShopFailure,
	fetchShopMigrationStatusRequest,
	fetchShopMigrationStatusSuccess,
	fetchShopMigrationStatusFailure,
	restartShopMigrationRequest,
	restartShopMigrationSuccess,
	restartShopMigrationFailure,
} from './reducer';
import { refreshTokenByTimeOutSuccess, refreshTokenByTimeOutRequest } from '../auth/reducer';
import {
	TShopGoodsRequest,
	TShopGoodsResponsePayload,
	TShopRequestPayload,
	TShopResponsePayload,
	TShopDeleteRequestPayload,
	TShopAttributesFormRequestPayload,
	TShopAttributesFormPayload,
	TShopAddResponsePayload,
	TShopAttributesRequestPayload,
	TShopAttributesResponsePayload,
	TShopMigrateRequestPayload,
	TShopMigrateResponsePayload,
	TShopMigrationStatusRequestPayload,
	TShopMigrationStatusResponsePayload,
	TShopRestartMigrationRequestPayload,
	TShopRestartMigrationResponsePayload,
} from './types';

function* getShopsWorker(action: PayloadAction<any>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TShopResponsePayload = yield call(api.shops.getShops, action.payload);
		yield put(getShopsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(error?.response?.data?.message ?? error.message ?? 'Error', 'error');
		}
	}
}

function* getShopGoodsWorker(action: PayloadAction<TShopGoodsRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TShopGoodsResponsePayload = yield call(api.shops.getShopGoods, action.payload);
		yield put(getShopGoodsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(error?.response?.data?.message ?? error.message ?? 'Error', 'error');
		}
	}
}

function* addShopWorker(action: PayloadAction<TShopRequestPayload>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TShopAddResponsePayload = yield call(api.shops.addShop, action.payload);
		yield put(addShopSuccess(response));
		console.log('addShopWorker response', response);
		if (response?.id) {
			yield put(getShopAttributesFormRequest({ shopId: response.id }));
		}
		yield put(getShopsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			yield put(addShopFailure(error?.response?.data?.message ?? error.message ?? 'Error'));
			notificationContainer(error?.response?.data?.message ?? error.message ?? 'Error', 'error');
		}
	}
}

function* deleteShopWorker(action: PayloadAction<TShopDeleteRequestPayload>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.shops.deleteShop, action.payload.shopId);
		yield put(deleteShopSuccess());
		yield put(getShopsRequest());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(error?.response?.data?.message ?? error.message ?? 'Error', 'error');
		}
	}
}

function* getShopAttributesFormWorker(action: PayloadAction<TShopAttributesFormRequestPayload>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		console.log('getShopAttributesFormWorker', action.payload.shopId);

		const response: TShopAttributesFormPayload = yield call(
			api.shops.getShopAttributesForm,
			action.payload.shopId,
		);
		yield put(getShopAttributesFormSuccess(response));
	} catch (error) {
		yield put(getShopAttributesFormFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(error?.response?.data?.message ?? error.message ?? 'Error', 'error');
		}
	}
}

function* updateShopAttributesWorker(action: PayloadAction<TShopAttributesRequestPayload>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		console.log('updateShopAttributesWorker', action.payload);

		const response: TShopAttributesResponsePayload = yield call(
			api.shops.updateShopAttributes,
			action.payload,
		);
		yield put(updateShopAttributesSuccess(response));
		yield put(getShopsRequest());
	} catch (error) {
		yield put(updateShopAttributesFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(error?.response?.data?.message ?? error.message ?? 'Error', 'error');
		}
	}
}

function* migrateShopWorker(action: PayloadAction<TShopMigrateRequestPayload>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		console.log('migrateShopWorker', action.payload);

		const response: TShopMigrateResponsePayload = yield call(
			api.shops.migrateShop,
			action.payload.shopId,
		);
		yield put(migrateShopSuccess(response));
		yield put(getShopsRequest());
	} catch (error) {
		yield put(migrateShopFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(error?.response?.data?.message ?? error.message ?? 'Error', 'error');
		}
	}
}

function* fetchShopMigrationStatusWorker(
	action: PayloadAction<TShopMigrationStatusRequestPayload>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		console.log('fetchShopMigrationStatusWorker', action.payload);

		const response: TShopMigrationStatusResponsePayload = yield call(
			api.shops.getShopMigrationStatus,
			action.payload.shopId,
		);
		yield put(
			fetchShopMigrationStatusSuccess({
				shopId: action.payload.shopId,
				status: response as number,
			}),
		);
	} catch (error) {
		yield put(fetchShopMigrationStatusFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(error?.response?.data?.message ?? error.message ?? 'Error', 'error');
		}
	}
}

function* restartShopMigrationWorker(action: PayloadAction<TShopRestartMigrationRequestPayload>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TShopRestartMigrationResponsePayload = yield call(
			api.shops.restartShopMigration,
			action.payload.shopId,
		);
		yield put(restartShopMigrationSuccess(response));
		yield put(getShopsRequest());
	} catch (error) {
		yield put(restartShopMigrationFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(error?.response?.data?.message ?? error.message ?? 'Error', 'error');
		}
	}
}

export function* shopsSaga() {
	yield takeEvery(getShopsRequest.type, getShopsWorker);
	yield takeEvery(getShopGoodsRequest.type, getShopGoodsWorker);
	yield takeEvery(addShopRequest.type, addShopWorker);
	yield takeEvery(deleteShopRequest.type, deleteShopWorker);
	yield takeEvery(getShopAttributesFormRequest.type, getShopAttributesFormWorker);
	yield takeEvery(updateShopAttributesRequest.type, updateShopAttributesWorker);
	yield takeEvery(migrateShopRequest.type, migrateShopWorker);
	yield takeEvery(fetchShopMigrationStatusRequest.type, fetchShopMigrationStatusWorker);
	yield takeEvery(restartShopMigrationRequest.type, restartShopMigrationWorker);
}
